import {
  AttachmentFragment,
  AttachmentsDocument,
  AttachmentsQuery,
  isError,
  useAttachmentCreatedSubscription,
  useAttachmentsQuery,
  withRequiredVariables,
  withStandardListHook,
} from '@spotify/confidence-graphql';

type UseAttachmentsVariables = {
  resource: string;
};

const useAttachmentsImpl = withStandardListHook<
  AttachmentsQuery,
  AttachmentFragment,
  'attachments',
  UseAttachmentsVariables
>(withRequiredVariables(useAttachmentsQuery), 'attachments', {
  query: AttachmentsDocument,
  typename: 'CollaborationV1ListAttachmentsResponse',
});

// Polling provides live updates while the attachmentCreated subscription
// isn't delivering events from the backend. The subscription
// handler stays wired so we pick up events the moment delivery resumes.
const DEFAULT_POLL_INTERVAL_MS = 30_000;

export const useAttachments: typeof useAttachmentsImpl = options => {
  useAttachmentCreatedSubscription({
    onData({ data: { data: event }, client }) {
      const attachment = event?.attachmentCreated?.attachment;
      if (!attachment || attachment.resource !== options.resource) {
        return;
      }

      client.cache.updateQuery<AttachmentsQuery>(
        {
          query: AttachmentsDocument,
          variables: {
            resource: options.resource,
            filter: options.filter ?? null,
            pageSize: options.pageSize ?? null,
            orderBy: options.orderBy ?? null,
          },
        },
        cachedData => updateAttachmentsQuery(cachedData, attachment),
      );
    },
  });

  return useAttachmentsImpl({
    pollInterval: DEFAULT_POLL_INTERVAL_MS,
    ...options,
  });
};

function updateAttachmentsQuery(
  data: AttachmentsQuery | null,
  attachment: AttachmentFragment,
): AttachmentsQuery | null | undefined {
  if (!data || isError(data?.attachments)) return undefined;

  const attachments = data.attachments?.attachments ?? [];

  if (attachments.find(a => a.name === attachment.name)) return undefined;

  return {
    ...data,
    attachments: {
      ...data.attachments,
      __typename: 'CollaborationV1ListAttachmentsResponse',
      attachments: [attachment, ...attachments],
    },
  };
}
